import { useState } from "react";

import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "@similarweb/ui-components";

import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import { translate } from "app/filters/i18nFilter";
import ApplyTopicModal from "app/pages/sales-intelligence/pages/manage-signals/components/ApplyTopicModal/ApplyTopicModal";
import ModalContentError from "app/pages/sales-intelligence/pages/manage-signals/components/ApplyTopicModal/modalContent/ModalContentError";
import ModalContentFinalStep from "app/pages/sales-intelligence/pages/manage-signals/components/ApplyTopicModal/modalContent/ModalContentFinalStep";
import TermsAndConditionsContent from "app/pages/sales-intelligence/pages/manage-signals/components/ApplyTopicModal/modalContent/TermsAndConditionsContent";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import intentTrackingService from "app/pages/sales-intelligence/services/tracking/intent/trackingService";
import { setSaveTopicsError } from "app/pages/sales-intelligence/sub-modules/intent/store/management/actions/actions";
import { saveIntentTopicsThunk } from "app/pages/sales-intelligence/sub-modules/intent/store/management/effects/effects";
import {
    selectIntentSaveTopicsError,
    selectIntentSelectedTopics,
    selectIsLocked,
    selectIsSelectedIntentTopicsOverLimit,
} from "app/pages/sales-intelligence/sub-modules/intent/store/management/selectors/selectors";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";

import { StyledApplyTopicBlock } from "./styles";

const ApplyTopicView = () => {
    const dispatch = useDispatch();
    const selectedTopics = useSelector(selectIntentSelectedTopics);
    const hasExtraTopic = useSelector(selectIsSelectedIntentTopicsOverLimit);
    const isLocked = useSelector(selectIsLocked);
    const saveTopicsError = useSelector(selectIntentSaveTopicsError);

    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [isFinalModalStep, setIsFinalModalStep] = useState<boolean>(false);
    const [intentCreatedSignalId, setIntentCreatedSignalId] = useState<string | null>(null);

    const getApplyTopicModalConfig = () => {
        if (saveTopicsError) {
            return {
                content: {
                    padding: "20px 16px 16px 24px",
                    width: "526px",
                },
            };
        }
        if (isFinalModalStep) {
            return {
                content: {
                    padding: "44px 68px",
                    width: "641px",
                },
            };
        }
        return {
            content: {
                padding: "20px 16px 16px 24px",
                width: "550px",
            },
        };
    };

    const onCloseModal = () => {
        if (!isFinalModalStep) {
            void intentTrackingService.trackApplyTopicClickAction("keep editing");
        }

        setIsOpenModal(false);
        debounce(() => {
            setIsFinalModalStep(false);
        }, 500);
    };

    const onCloseErrorModal = () => {
        onCloseModal();
        dispatch(setSaveTopicsError(undefined));
    };

    const numIntentTopics = salesSettingsHelper.getNumIntentTopics();
    const isMaxSelectedTopics = selectedTopics.length >= numIntentTopics;

    const handleGoToAdvancedSearch = () => {
        siNavigator.goToAdvancedSearchPage({}, { signalId: intentCreatedSignalId || undefined });
    };

    const onSaveSelectedTopics = async () => {
        const data = await dispatch(
            saveIntentTopicsThunk({ topics: selectedTopics.map((topic: Topic) => topic.name) }),
        );

        if (!saveTopicsError && data) {
            void intentTrackingService.trackApplyTopicClickAction("apply topics", selectedTopics.length);
            setIntentCreatedSignalId(data.payload.defaultIntentSignalId);

            setIsOpenModal(true);
            setIsFinalModalStep(true);
        }
    };

    const onOpenModal = () => {
        setIsFinalModalStep(false);
        setIsOpenModal(true);
    };

    const isBlockedButton = selectedTopics.length === 0 || hasExtraTopic || isLocked;

    const getTooltipText = () => {
        if (isLocked) {
            return translate("si.intent.management.tooltip.locked-not-admin");
        }

        if (hasExtraTopic) {
            return translate("si.intent.management.tooltip.has-extra-topics");
        }

        if (selectedTopics.length === 0) {
            return translate("si.intent.management.tooltip.empty-selected-topics");
        }

        return translate("si.intent.management.tooltip.locked-btn");
    };

    const getModalContainer = () => {
        if (saveTopicsError) {
            return (
                <ModalContentError
                    title={translate("si.intent.management.modal.error.title")}
                    description={translate("si.intent.management.modal.error.description")}
                    onClick={onCloseErrorModal}
                    buttonText={translate("si.intent.management.modal.btn.got-it")}
                />
            );
        }

        if (isFinalModalStep) {
            return (
                <ModalContentFinalStep
                    title={translate("si.intent.management.modal.header.success-save-topic")}
                    description={translate("si.intent.management.modal.description.success-save-topic")}
                    onClick={handleGoToAdvancedSearch}
                    buttonText={translate("si.intent.management.modal.btn.discover-companies-intent")}
                />
            );
        }

        if (isMaxSelectedTopics) {
            return (
                <TermsAndConditionsContent
                    title={translate("si.intent.management.modal.header.apply-topics")}
                    description={translate("si.intent.management.modal.description.apply-topics")}
                    cancelButtonText={translate("si.intent.management.modal.btn.keep-editing")}
                    applyButtonText={translate("si.intent.management.modal.btn.apply-topics")}
                    onClick={onSaveSelectedTopics}
                    onCancelClick={onCloseModal}
                />
            );
        }

        return (
            <TermsAndConditionsContent
                title={translate("si.intent.management.modal.header.partial-topics-amount")}
                description={translate("si.intent.management.modal.description.partial-topics-amount")}
                cancelButtonText={translate("si.intent.management.modal.btn.apply-topics")}
                applyButtonText={translate("si.intent.management.modal.btn.keep-editing")}
                onClick={onCloseModal}
                onCancelClick={onSaveSelectedTopics}
            />
        );
    };

    return (
        <StyledApplyTopicBlock>
            <PlainTooltip
                text={getTooltipText()}
                placement="top"
                enabled={isBlockedButton}
                maxWidth={167}
            >
                <div>
                    <Button
                        type="primary"
                        isDisabled={isBlockedButton}
                        onClick={onSaveSelectedTopics}
                    >
                        {translate("si.intent.management.btn.apply-topics")}
                    </Button>
                </div>
            </PlainTooltip>
            <ApplyTopicModal
                isOpen={isOpenModal}
                customStyle={getApplyTopicModalConfig()}
                showCloseIcon={isFinalModalStep || false}
                onClose={onCloseModal}
            >
                {getModalContainer()}
            </ApplyTopicModal>
        </StyledApplyTopicBlock>
    );
};

export default ApplyTopicView;
